<template>
  <div class="page clients-page">
    <div class="page-header">
      <h1 class="page-title">Clientes</h1>
      <p class="page-subtitle">Cadastro de clientes</p>
    </div>

    <div class="clients-search-wrap">
      <label for="client-search" class="clients-search-label">Buscar</label>
      <input
        id="client-search"
        v-model="searchQuery"
        type="search"
        class="clients-search-input"
        placeholder="Nome, apelido ou telefone"
        aria-label="Buscar por nome, apelido ou telefone"
        autocomplete="off"
      />
      <i class="material-icons clients-search-icon" aria-hidden="true">search</i>
    </div>

    <div class="card-list">
      <article
        v-for="client in filteredClients"
        :key="client.id"
        class="card card--with-icon"
      >
        <div class="card-icon-wrap">
          <i class="material-icons">person</i>
        </div>
        <div class="card-content">
          <h3 class="card-title card-primary">{{ client.name }}</h3>
          <p v-if="canSeeClientMeta && client.apelido" class="card-meta card-secondary">
            <i class="material-icons card-meta-icon">badge</i>
            {{ client.apelido }}
          </p>
          <p v-if="canSeeClientMeta && client.phone" class="card-meta card-secondary">
            <i class="material-icons card-meta-icon">phone</i>
            {{ formatPhoneDisplay(client.phone) }}
          </p>
          <p v-if="canSeeClientMeta && client.instagram" class="card-meta card-accent">
            @{{ client.instagram }}
          </p>
          <p v-if="canSeeClientMeta && client.observation" class="card-meta card-secondary card-meta--observation">
            {{ client.observation }}
          </p>
        </div>
        <div v-if="canSeeClientMeta" class="card-actions">
          <button
            type="button"
            class="btn-icon"
            aria-label="Editar"
            @click="handleEditClick(client.id)"
          >
            <i class="material-icons">edit</i>
          </button>
          <button
            type="button"
            class="btn-icon btn-icon-danger"
            aria-label="Excluir"
            @click="handleDeleteClick(client.id)"
          >
            <i class="material-icons">delete_outline</i>
          </button>
        </div>
      </article>
    </div>

    <button
      type="button"
      class="btn-fab"
      @click="handleAddClick"
      aria-label="Novo cliente"
    >
      <i class="material-icons">add</i>
    </button>

    <Drawer :model-value="showModal" @update:model-value="showModal = $event">
      <div class="modal-header">
        <h2 class="modal-title">{{ selectedId ? 'Alterar cliente' : 'Novo cliente' }}</h2>
        <button
          type="button"
          class="modal-close"
          aria-label="Fechar"
          @click="showModal = false"
        >
          <i class="material-icons">close</i>
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="save">
          <div class="form-group">
            <label for="name">Nome</label>
            <input
              id="name"
              type="text"
              name="name"
              v-model="name"
              :aria-invalid="!!nameError"
              aria-describedby="name-error"
            />
            <p v-if="nameError" id="name-error" class="field-error" role="alert">{{ nameError }}</p>
          </div>
          <div class="form-group">
            <label for="apelido">Apelido</label>
            <input
              id="apelido"
              type="text"
              name="apelido"
              v-model="apelido"
              placeholder="Nome pelo qual a cliente prefere ser chamada"
            />
          </div>
          <div class="form-group">
            <label for="phone">Telefone</label>
            <PhoneInput
              id="phone"
              v-model="phone"
              :invalid="!!phoneError"
              aria-describedby="phone-error"
              aria-label="Telefone"
            />
            <p v-if="phoneError" id="phone-error" class="field-error" role="alert">{{ phoneError }}</p>
          </div>
          <div class="form-group">
            <label for="instagram">Instagram</label>
            <input id="instagram" type="text" name="instagram" v-model="instagram" />
          </div>
          <div class="form-group">
            <label for="observation">Observação</label>
            <textarea
              id="observation"
              name="observation"
              v-model="observation"
              class="observation-input"
              rows="3"
              placeholder="Observações sobre a cliente"
            />
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" :disabled="saveLoading" @click="showModal = false">
          Cancelar
        </button>
        <button type="button" class="btn btn-primary" :disabled="saveLoading" @click="save">
          {{ saveLoading ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
    </Drawer>

    <ConfirmDialog
      v-model="showDeleteConfirm"
      title="Excluir cliente"
      message="Tem certeza que deseja excluir esta cliente? Esta ação não pode ser desfeita."
      confirm-label="Excluir"
      :confirm-loading="deleteLoading"
      @confirm="doDeleteClient"
    />
  </div>
</template>

<script>
import Drawer from '@/components/Drawer.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import PhoneInput from '@/components/PhoneInput.vue';
import { doc, collection, deleteDoc, setDoc, addDoc } from 'firebase/firestore';
import { firestore } from '@/firebase';
import { ref, computed, watch } from 'vue';
import { useClientStore } from '@/stores/client';
import { useUserStore } from '@/stores/user';
import { useToast } from '@/composables/useToast';
import { normalizeToRaw, formatPhoneForDisplay } from '@/utils/phoneUtils';

export default {
  name: 'Client',
  components: { Drawer, ConfirmDialog, PhoneInput },
  setup() {
    const clientStore = useClientStore();
    const userStore = useUserStore();
    const { show: showToast } = useToast();
    const searchQuery = ref('');
    const showModal = ref(false);
    const showDeleteConfirm = ref(false);
    const deleteTargetId = ref(null);
    const saveLoading = ref(false);
    const deleteLoading = ref(false);
    const nameError = ref('');
    const phoneError = ref('');
    const selectedId = ref(null);
    const name = ref('');
    const apelido = ref('');
    const phone = ref('');
    const instagram = ref('');
    const observation = ref('');

    const handleAddClick = () => {
      nameError.value = '';
      phoneError.value = '';
      selectedId.value = null;
      name.value = '';
      apelido.value = '';
      phone.value = '';
      instagram.value = '';
      observation.value = '';
      showModal.value = true;
    };

    const handleEditClick = (id) => {
      nameError.value = '';
      phoneError.value = '';
      selectedId.value = id;
      const c = clientStore.clients[id];
      name.value = c.name;
      apelido.value = c.apelido ?? '';
      phone.value = normalizeToRaw(c.phone) ?? c.phone ?? '';
      instagram.value = c.instagram ?? '';
      observation.value = c.observation ?? '';
      showModal.value = true;
    };

    const handleDeleteClick = (id) => {
      deleteTargetId.value = id;
      showDeleteConfirm.value = true;
    };

    const doDeleteClient = async () => {
      if (!deleteTargetId.value) return;
      deleteLoading.value = true;
      try {
        await deleteDoc(doc(firestore, 'clients', deleteTargetId.value));
        deleteTargetId.value = null;
        showDeleteConfirm.value = false;
        showToast('Cliente excluído.', 'success');
      } catch (err) {
        showToast('Não foi possível excluir. Tente novamente.', 'error');
      } finally {
        deleteLoading.value = false;
      }
    };

    watch(name, () => { nameError.value = ''; });
    watch(phone, () => { phoneError.value = ''; });

    function formatPhoneDisplay(raw) {
      const normalized = normalizeToRaw(raw) || raw || '';
      return formatPhoneForDisplay(normalized);
    }

    const save = async () => {
      nameError.value = '';
      phoneError.value = '';
      let valid = true;
      if (!name.value || !name.value.trim()) {
        nameError.value = 'Informe o nome da cliente.';
        valid = false;
      }
      const phoneRaw = normalizeToRaw(phone.value) ?? phone.value ?? '';
      if (!valid) return;
      saveLoading.value = true;
      try {
        const docData = {
          name: name.value.trim(),
          apelido: apelido.value.trim(),
          phone: phoneRaw,
          instagram: instagram.value.trim(),
          observation: observation.value.trim(),
        };
        if (selectedId.value) {
          await setDoc(doc(firestore, 'clients', selectedId.value), docData, { merge: true });
        } else {
          await addDoc(collection(firestore, 'clients'), docData);
        }
        showModal.value = false;
        showToast(selectedId.value ? 'Cliente alterado.' : 'Cliente salvo.', 'success');
      } catch (err) {
        showToast('Não foi possível salvar. Tente novamente.', 'error');
      } finally {
        saveLoading.value = false;
      }
    };

    const sortByName = (a, b) => {
      const x = (a.name || '').toUpperCase();
      const y = (b.name || '').toUpperCase();
      return x < y ? -1 : x > y ? 1 : 0;
    };

    const clients = computed(() => {
      const list = Object.entries(clientStore.clients).map(([id, data]) => ({ id, ...data }));
      return list.sort((a, b) => sortByName(a, b));
    });

    const filteredClients = computed(() => {
      const q = (searchQuery.value || '').trim().toLowerCase().replace(/\s+/g, '');
      if (!q) return clients.value;
      const digitsOnly = q.replace(/\D/g, '');
      return clients.value.filter((client) => {
        const nameNorm = (client.name || '').toLowerCase().replace(/\s+/g, '');
        const apelidoNorm = (client.apelido || '').toLowerCase().replace(/\s+/g, '');
        const phoneRaw = (client.phone || '').replace(/\D/g, '');
        const matchesName = nameNorm.includes(q);
        const matchesApelido = apelidoNorm.includes(q);
        const matchesPhone = digitsOnly.length >= 2 && phoneRaw.includes(digitsOnly);
        return matchesName || matchesApelido || matchesPhone;
      });
    });

    const canSeeClientMeta = computed(() => userStore.hasPermission('clients:manage'));

    return {
      name,
      apelido,
      phone,
      instagram,
      observation,
      searchQuery,
      selectedId,
      showModal,
      saveLoading,
      deleteLoading,
      save,
      formatPhoneDisplay,
      handleAddClick,
      handleEditClick,
      handleDeleteClick,
      doDeleteClient,
      showDeleteConfirm,
      nameError,
      phoneError,
      canSeeClientMeta,
      filteredClients,
    };
  },
};
</script>

<style scoped>
.clients-page {
  padding: var(--space-md) 0;
}

.clients-search-wrap {
  position: relative;
  margin-bottom: var(--space-lg);
}

.clients-search-label {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.clients-search-input {
  width: 100%;
  padding: 10px 12px 10px 44px;
  font-size: 1rem;
  font-family: inherit;
  color: var(--color-text);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.clients-search-input:focus {
  outline: none;
  border-color: var(--color-border-focus);
  box-shadow: 0 0 0 3px var(--color-accent-soft);
}

.clients-search-input::placeholder {
  color: var(--color-text-muted);
}

.clients-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 22px;
  color: var(--color-text-muted);
  pointer-events: none;
}

.field-error {
  font-size: 0.875rem;
  color: var(--color-error);
  margin-top: var(--space-xs);
  margin-bottom: 0;
}

.observation-input {
  width: 100%;
  padding: 10px 12px;
  font-family: inherit;
  font-size: 0.95rem;
  color: var(--color-text);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  resize: vertical;
}

.observation-input:focus {
  outline: none;
  border-color: var(--color-border-focus);
  box-shadow: 0 0 0 3px var(--color-accent-soft);
}

.card-meta--observation {
  font-style: italic;
  margin-top: var(--space-xs);
}
</style>
